import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Activity, Database, Cpu, Search, RefreshCw } from 'lucide-react';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { Card, CardContent } from '../components/shadcn/card';
import { Badge } from '../components/shadcn/badge';
import { Button } from '../components/shadcn/button';

interface ServiceStatus {
  status: string;
  responseTime?: number;
  error?: string;
  lastActivity?: string;
  pendingJobs?: number;
  lastScan?: string;
  totalScans?: number;
}

interface StatusData {
  overall: 'operational' | 'degraded' | 'down';
  timestamp: string;
  uptime: number;
  version?: string;
  services: {
    api: ServiceStatus;
    database: ServiceStatus;
    worker: ServiceStatus;
    scanners: ServiceStatus;
  };
  stats: {
    totalRepos?: number;
    totalFindings?: number;
    totalScans?: number;
    lastScanAt?: string;
  };
}

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'operational':
      return <CheckCircle className="w-5 h-5 text-success" />;
    case 'degraded':
      return <AlertTriangle className="w-5 h-5 text-medium" />;
    case 'down':
      return <XCircle className="w-5 h-5 text-critical" />;
    default:
      return <Activity className="w-5 h-5 text-muted animate-pulse" />;
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const variantMap: Record<string, "default" | "secondary" | "destructive" | "outline" | "critical" | "high" | "medium" | "low"> = {
    operational: 'default',
    degraded: 'medium',
    down: 'critical',
    unknown: 'secondary',
    checking: 'low',
    idle: 'secondary',
  };

  const displayStatus = status === 'unknown' ? 'Idle' : status.charAt(0).toUpperCase() + status.slice(1);
  const colorKey = status === 'unknown' ? 'idle' : status;

  return (
    <Badge variant={variantMap[colorKey] || 'secondary'}>
      {displayStatus}
    </Badge>
  );
};

const ServiceCard: React.FC<{
  name: string;
  icon: React.ReactNode;
  service: ServiceStatus;
  description: string;
}> = ({ name, icon, service, description }) => {
  const displayStatus = service.status === 'unknown' ? 'idle' : service.status;

  const borderColorMap: Record<string, string> = {
    operational: 'border-l-4 border-l-success',
    degraded: 'border-l-4 border-l-medium',
    down: 'border-l-4 border-l-critical',
    idle: 'border-l-4 border-l-muted',
    unknown: 'border-l-4 border-l-muted',
    checking: 'border-l-4 border-l-info',
  };

  const leftBorder = borderColorMap[service.status] || borderColorMap['idle'];

  return (
    <Card className={`border-border/30 bg-surface1 hover:border-border/60 transition-all ${leftBorder}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-surface2/80 rounded-lg">
              {icon}
            </div>
            <div>
              <h3 className="font-semibold text-text">{name}</h3>
              <p className="text-sm text-muted">{description}</p>
            </div>
          </div>
          <StatusIcon status={displayStatus === 'idle' ? 'operational' : displayStatus} />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted">Status</span>
            <StatusBadge status={service.status} />
          </div>

          {service.responseTime !== undefined && (
            <div className="flex justify-between text-sm">
              <span className="text-muted">Response Time</span>
              <span className="font-mono text-text">{service.responseTime}ms</span>
            </div>
          )}

          {service.pendingJobs !== undefined && service.pendingJobs > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted">Pending Jobs</span>
              <span className="font-mono text-text">{service.pendingJobs}</span>
            </div>
          )}

          {service.error && (
            <div className="mt-2 p-2 bg-critical/10 border border-critical/20 rounded text-sm text-critical">
              {service.error}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const calculateOverallStatus = (status: StatusData): 'operational' | 'degraded' | 'down' => {
  const { api, database, worker, scanners } = status.services;

  if (api.status === 'down' || database.status === 'down') {
    return 'down';
  }

  const workerOk = worker.status === 'operational' || worker.status === 'unknown';
  const scannersOk = scanners.status === 'operational' || scanners.status === 'unknown';

  if (api.status === 'degraded' || database.status === 'degraded') {
    return 'degraded';
  }

  if (!workerOk || !scannersOk) {
    return 'degraded';
  }

  return 'operational';
};

const StatusPage: React.FC = () => {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const { apiFetch } = await import('../api/client');
      const data = await apiFetch<any>({ path: '/v1/status' });
      setStatus(data);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const overall = status ? calculateOverallStatus(status) : 'operational';

  const overallBadgeVariant: Record<string, "default" | "medium" | "critical"> = {
    operational: 'default',
    degraded: 'medium',
    down: 'critical',
  };

  const overallColors: Record<string, { bg: string; border: string; text: string }> = {
    operational: {
      bg: 'bg-success/10',
      border: 'border-success/30',
      text: 'text-success',
    },
    degraded: {
      bg: 'bg-medium/10',
      border: 'border-medium/30',
      text: 'text-medium',
    },
    down: {
      bg: 'bg-critical/10',
      border: 'border-critical/30',
      text: 'text-critical',
    },
  };

  const colors = overallColors[overall] || overallColors.operational;

  return (
    <MarketingLayout>
      <div className="pt-24 pb-6 flex items-center justify-end max-w-7xl mx-auto px-6">
        <Button
          variant="outline"
          size="sm"
          onClick={fetchStatus}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Hero with gradient */}
      <div className="bg-gradient-to-b from-brand/5 via-bg to-bg">
        <div className="max-w-5xl mx-auto px-6 py-10 text-center">
          <h1 className="text-3xl font-bold text-text mb-2">System Status</h1>
          <p className="text-muted">Real-time health monitoring for all Paxley services</p>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-8 relative z-10">
        {/* Overall Status Banner */}
        <Card className={`${colors.bg} ${colors.border} mb-8`}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <StatusIcon status={overall} />
              <div>
                <h2 className={`text-2xl font-bold ${colors.text}`}>
                  {overall === 'operational' && 'All Systems Operational'}
                  {overall === 'degraded' && 'Degraded Performance'}
                  {overall === 'down' && 'Service Disruption'}
                </h2>
                <p className="text-sm text-muted mt-1">
                  Last checked: {lastRefresh.toLocaleTimeString()}
                  {status?.uptime ? ` \u2022 Uptime: ${formatUptime(status.uptime)}` : ''}
                  {status?.version ? ` \u2022 Build: ${status.version}` : ''}
                </p>
              </div>
              <div className="ml-auto">
                <Badge variant={overallBadgeVariant[overall] || 'default'}>
                  {overall.charAt(0).toUpperCase() + overall.slice(1)}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="bg-critical/10 border-critical/30 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-critical" />
                <div>
                  <h3 className="font-semibold text-critical">Failed to load status</h3>
                  <p className="text-sm text-critical/80">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Services Grid */}
        {status && (
          <div className="grid md:grid-cols-2 gap-6">
            <ServiceCard
              name="API Server"
              icon={<Activity className="w-5 h-5 text-brand" />}
              service={status.services.api}
              description="REST API & Authentication"
            />

            <ServiceCard
              name="Database"
              icon={<Database className="w-5 h-5 text-info" />}
              service={status.services.database}
              description="Azure Postgres Flexible Server"
            />

            <ServiceCard
              name="Scan Worker"
              icon={<Cpu className="w-5 h-5 text-success" />}
              service={status.services.worker}
              description="Job Processing Engine"
            />

            <ServiceCard
              name="Security Scanners"
              icon={<Search className="w-5 h-5 text-high" />}
              service={status.services.scanners}
              description="SAST, Dependencies, Secrets, IaC"
            />
          </div>
        )}

        {/* Operational stats — only render when the API has at least one
            real number; keeps the UI quiet on a fresh deploy. */}
        {status && (
          status.stats.totalRepos !== undefined ||
          status.stats.totalScans !== undefined ||
          status.stats.totalFindings !== undefined
        ) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {status.stats.totalRepos !== undefined && (
              <Card className="border-border/30 bg-surface1">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted uppercase tracking-wide">Repos</p>
                  <p className="text-2xl font-semibold text-text mt-1">{status.stats.totalRepos}</p>
                </CardContent>
              </Card>
            )}
            {status.stats.totalScans !== undefined && (
              <Card className="border-border/30 bg-surface1">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted uppercase tracking-wide">Scans completed</p>
                  <p className="text-2xl font-semibold text-text mt-1">{status.stats.totalScans}</p>
                </CardContent>
              </Card>
            )}
            {status.stats.totalFindings !== undefined && (
              <Card className="border-border/30 bg-surface1">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted uppercase tracking-wide">Open findings</p>
                  <p className="text-2xl font-semibold text-text mt-1">{status.stats.totalFindings}</p>
                </CardContent>
              </Card>
            )}
            {status.stats.lastScanAt && (
              <Card className="border-border/30 bg-surface1">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted uppercase tracking-wide">Last scan</p>
                  <p className="text-sm font-medium text-text mt-2">
                    {new Date(status.stats.lastScanAt).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-muted">
          <p>Powered by <span className="text-brand font-medium">Paxley</span> &bull; Running on Azure</p>
          <p className="mt-2">
            <a href="/" className="text-brand hover:text-text transition-colors">
              &larr; Back to Dashboard
            </a>
          </p>
        </div>
      </main>
    </MarketingLayout>
  );
};

export default StatusPage;
